/**
 * Integrations Settings Page
 *
 * Provides tracking code snippets for various platforms.
 * Users can copy these snippets to integrate Armenian Link
 * conversion tracking into their websites and apps.
 *
 * @module pages/integrations-settings
 *
 * @remarks
 * Supported integrations:
 * - JavaScript SDK (universal snippet)
 * - Google Tag Manager
 * - WordPress
 * - Shopify
 * - React/Next.js
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Copy,
  Check,
  Code2,
  Globe,
  ShoppingBag,
  FileCode,
  Blocks,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { SettingsLayout } from '@/client/components/layout/settings-layout';

// =============================================================================
// Code Snippets
// =============================================================================

const TRACKING_DOMAIN = 'arml.ink';

/**
 * JavaScript SDK snippet - universal tracking code
 * This is the primary integration method for most websites.
 */
const jsSnippet = `<!-- Armenian Link Conversion Tracking -->
<script>
  (function(w,d,s,l,i){
    w[l]=w[l]||[];
    var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s);
    j.async=true;
    j.src='https://${TRACKING_DOMAIN}/t/sdk.js';
    j.onload=function(){
      window.ArmenianLink&&window.ArmenianLink.init();
    };
    f.parentNode.insertBefore(j,f);
  })(window,document,'script','armenianLink');
</script>`;

/**
 * Google Tag Manager custom HTML snippet
 */
const gtmSnippet = `<!-- Armenian Link - GTM Custom HTML Tag -->
<script>
  (function() {
    var script = document.createElement('script');
    script.src = 'https://${TRACKING_DOMAIN}/t/sdk.js';
    script.async = true;
    script.onload = function() {
      if (window.ArmenianLink) {
        window.ArmenianLink.init();
      }
    };
    document.head.appendChild(script);
  })();
</script>

<!--
  Trigger: Page View - All Pages
  Tag Type: Custom HTML
-->`;

/**
 * WordPress integration (via functions.php or plugin)
 */
const wordpressSnippet = `<?php
/**
 * Add Armenian Link tracking to WordPress
 * Add this to your theme's functions.php or create a custom plugin
 */
function armenian_link_tracking() {
  ?>
  <script>
    (function(w,d,s,l,i){
      w[l]=w[l]||[];
      var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s);
      j.async=true;
      j.src='https://${TRACKING_DOMAIN}/t/sdk.js';
      j.onload=function(){
        window.ArmenianLink&&window.ArmenianLink.init();
      };
      f.parentNode.insertBefore(j,f);
    })(window,document,'script','armenianLink');
  </script>
  <?php
}
add_action('wp_head', 'armenian_link_tracking');`;

/**
 * Shopify Liquid snippet (theme.liquid)
 */
const shopifySnippet = `{% comment %}
  Armenian Link Conversion Tracking
  Add to theme.liquid before </head>
{% endcomment %}
<script>
  (function(w,d,s,l,i){
    w[l]=w[l]||[];
    var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s);
    j.async=true;
    j.src='https://${TRACKING_DOMAIN}/t/sdk.js';
    j.onload=function(){
      window.ArmenianLink&&window.ArmenianLink.init();
    };
    f.parentNode.insertBefore(j,f);
  })(window,document,'script','armenianLink');
</script>

{% comment %}
  For purchase tracking, add to checkout success page:
  <script>
    ArmenianLink.trackConversion('purchase', {
      value: {{ checkout.total_price | money_without_currency }},
      currency: '{{ checkout.currency }}',
      orderId: '{{ order.name }}'
    });
  </script>
{% endcomment %}`;

/**
 * React/Next.js component integration
 */
const reactSnippet = `// components/ArmenianLinkTracker.tsx
'use client';

import { useEffect } from 'react';

declare global {
  interface Window {
    ArmenianLink?: {
      init: () => void;
      trackConversion: (
        type: string,
        data?: Record<string, unknown>
      ) => void;
    };
  }
}

/**
 * Armenian Link Tracking Component
 * Add to your root layout or _app.tsx
 */
export function ArmenianLinkTracker() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://${TRACKING_DOMAIN}/t/sdk.js';
    script.async = true;
    script.onload = () => {
      window.ArmenianLink?.init();
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return null;
}

// Usage in layout.tsx or _app.tsx:
// import { ArmenianLinkTracker } from '@/components/ArmenianLinkTracker';
//
// export default function RootLayout({ children }) {
//   return (
//     <html>
//       <body>
//         <ArmenianLinkTracker />
//         {children}
//       </body>
//     </html>
//   );
// }`;

/**
 * Manual conversion tracking API example
 */
const apiSnippet = `// Track conversions via JavaScript
window.ArmenianLink.trackConversion('signup', {
  value: 0,
  metadata: { plan: 'free' }
});

// Track purchase conversions
window.ArmenianLink.trackConversion('purchase', {
  value: 99.99,
  currency: 'USD',
  orderId: 'ORD-12345',
  metadata: {
    items: ['product-1', 'product-2'],
    coupon: 'SAVE10'
  }
});

// Track custom events
window.ArmenianLink.trackConversion('newsletter_signup', {
  metadata: { source: 'footer-form' }
});`;

// =============================================================================
// Integration Card Component
// =============================================================================

interface IntegrationCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  code: string;
  language: string;
  iconColor?: string;
}

function IntegrationCard({
  title,
  description,
  icon: Icon,
  code,
  language,
  iconColor = 'bg-primary',
}: IntegrationCardProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b bg-muted/30">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'w-10 h-10 rounded-lg flex items-center justify-center',
              iconColor
            )}
          >
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>

      {/* Code Block */}
      <div className="relative">
        <div className="absolute top-2 right-2 flex items-center gap-2">
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
            {language}
          </span>
          <button
            onClick={handleCopy}
            className={cn(
              'p-2 rounded-md transition-colors',
              copied
                ? 'bg-green-500/10 text-green-500'
                : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground'
            )}
            title={copied ? 'Copied!' : 'Copy to clipboard'}
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </div>
        <pre className="p-4 pt-12 overflow-x-auto text-sm bg-zinc-950 text-zinc-100">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export function IntegrationsSettingsPage() {
  return (
    <SettingsLayout>
      {/* Integrations Content */}
      <div className="space-y-8">
        {/* Introduction */}
        <div className="rounded-lg border bg-card p-6">
          <h2 className="text-lg font-semibold mb-2">Conversion Tracking</h2>
          <p className="text-muted-foreground">
            Track conversions from your Armenian Link short URLs. Add our tracking
            snippet to your website to measure the effectiveness of your campaigns
            and understand which links drive the most value.
          </p>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <a
              href="/docs#tracking"
              className="inline-flex items-center gap-1.5 text-primary hover:underline"
            >
              <FileCode className="h-4 w-4" />
              View Documentation
            </a>
            <a
              href="/docs#api-reference"
              className="inline-flex items-center gap-1.5 text-primary hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              API Reference
            </a>
          </div>
        </div>

        {/* Primary Integration */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Recommended Integration</h3>
          <IntegrationCard
            title="JavaScript SDK"
            description="Universal tracking snippet for any website"
            icon={Code2}
            code={jsSnippet}
            language="HTML"
            iconColor="bg-amber-500"
          />
        </div>

        {/* Platform-Specific */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Platform Integrations</h3>
          <div className="grid gap-6">
            <IntegrationCard
              title="Google Tag Manager"
              description="Custom HTML tag for GTM"
              icon={Blocks}
              code={gtmSnippet}
              language="HTML"
              iconColor="bg-blue-500"
            />

            <IntegrationCard
              title="WordPress"
              description="Add to functions.php or custom plugin"
              icon={Globe}
              code={wordpressSnippet}
              language="PHP"
              iconColor="bg-sky-600"
            />

            <IntegrationCard
              title="Shopify"
              description="Add to theme.liquid template"
              icon={ShoppingBag}
              code={shopifySnippet}
              language="Liquid"
              iconColor="bg-green-600"
            />

            <IntegrationCard
              title="React / Next.js"
              description="TypeScript component for React apps"
              icon={Blocks}
              code={reactSnippet}
              language="TypeScript"
              iconColor="bg-cyan-500"
            />
          </div>
        </div>

        {/* API Usage */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Tracking Conversions</h3>
          <IntegrationCard
            title="Conversion API"
            description="Track custom conversion events"
            icon={Code2}
            code={apiSnippet}
            language="JavaScript"
            iconColor="bg-purple-500"
          />
        </div>

        {/* Help Section */}
        <div className="rounded-lg border border-dashed p-6">
          <h4 className="font-medium mb-2">Need help with integration?</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Check our documentation for detailed setup guides, troubleshooting tips,
            and advanced configuration options.
          </p>
          <div className="flex gap-3">
            <Link
              to="/docs"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            >
              <FileCode className="h-4 w-4" />
              View Docs
            </Link>
            <a
              href="mailto:support@armenian.link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium hover:bg-muted"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </SettingsLayout>
  );
}
